import { Link } from "react-router-dom";
import { ApiUnavailableState, LoadingState, NotConfiguredState } from "../components/states";
import { MetricCard } from "../components/dashboard";
import { Icon } from "../components/icons";
import { usePlatformStatus } from "../hooks/usePlatformStatus";
import { useAppContext } from "../hooks/useAppContext";

/**
 * Members — the connected server's member directory. Layout uses the ODIN
 * design system; the stat row and table are driven by real data with honest
 * empty states until a server is connected. No fabricated members or counts.
 */

const COLUMNS = ["Member", "Roles", "Joined", "Status"] as const;

export function Members(): JSX.Element {
  const status = usePlatformStatus();
  const { guildName } = useAppContext();
  const ready = status.kind === "ready" ? status.data : null;
  const connected = Boolean(guildName);

  return (
    <div className="ax-content" style={{ gridTemplateColumns: "1fr" }}>
      <div className="ax-workspace">
        <div className="ax-head">
          <div>
            <h1 className="ax-head__title">Members</h1>
            <p className="ax-head__sub">Your server&apos;s members, their roles and moderation history.</p>
          </div>
          <Link to="/app/servers" className="ax-btn ax-btn--secondary">
            {connected ? "Sync members" : "Connect a server"}
          </Link>
        </div>

        {status.kind === "loading" ? <LoadingState label="Contacting the control API…" /> : null}
        {status.kind === "not-configured" ? (
          <NotConfiguredState title="Control API URL not configured">
            Set <code>PUBLIC_API_URL</code> when building the dashboard to see live status here.
          </NotConfiguredState>
        ) : null}
        {status.kind === "unavailable" ? <ApiUnavailableState detail={status.detail} /> : null}

        {ready ? (
          <>
            <div className="ax-stats4">
              <MetricCard label="Total members" icon={<Icon.members />} value={null} foot={connected ? "Awaiting sync" : "No server"} />
              <MetricCard label="Online now" icon={<Icon.heart />} iconTone="success" value={null} foot={connected ? "Awaiting sync" : "No server"} />
              <MetricCard label="New this week" icon={<Icon.userPlus />} value={null} foot={connected ? "Awaiting sync" : "No server"} />
              <MetricCard label="Bots" icon={<Icon.botConfig />} value={null} foot={connected ? "Awaiting sync" : "No server"} />
            </div>

            <div className="ax-toolbar">
              <span className={`ax-input ax-input--disabled`} aria-disabled="true">
                <Icon.search />
                Search members…
              </span>
              <span className="ax-btn ax-btn--secondary" aria-disabled="true" style={{ opacity: 0.6, cursor: "not-allowed" }}>
                All roles
              </span>
              <span className="ax-btn ax-btn--secondary" aria-disabled="true" style={{ opacity: 0.6, cursor: "not-allowed" }}>
                Filters
              </span>
            </div>

            <div className="ax-table-wrap">
              <table className="ax-table">
                <thead>
                  <tr>
                    {COLUMNS.map((c) => (
                      <th key={c}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="ax-table__empty" colSpan={COLUMNS.length}>
                      {connected
                        ? "No members loaded yet — sync your server to populate this list."
                        : "Connect a Discord server to load its members."}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
